import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import {
  ArrowLeft, Award, Check, Send, MessageCircle, Loader2,
  BookOpen, Gavel, Users, CheckCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const JudgePanelPage = () => {
  const { id } = useParams();
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [picks, setPicks] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const [discussion, setDiscussion] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const discussionEndRef = useRef(null);

  const fetchPanel = useCallback(async () => {
    try {
      const res = await api.get(`/challenges/${id}/judge-panel`);
      setPanel(res.data);
      setPicks(res.data.my_picks || {});
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("You don't have judge access to this challenge.");
        navigate('/challenges');
      }
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  const fetchDiscussion = useCallback(async () => {
    try {
      const res = await api.get(`/challenges/${id}/judge-discussion`);
      setDiscussion(res.data);
    } catch { /* non-critical */ }
  }, [api, id]);

  useEffect(() => {
    fetchPanel();
    fetchDiscussion();
  }, [fetchPanel, fetchDiscussion]);

  useEffect(() => {
    if (discussionEndRef.current) {
      discussionEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [discussion]);

  const handlePick = async (category, entryId) => {
    setSubmitting(category);
    try {
      await api.post(`/challenges/${id}/judge-pick`, { category, entry_id: entryId });
      setPicks(prev => ({ ...prev, [category]: entryId }));
      toast.success(`Your pick for "${category}" has been saved.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pick.');
    } finally {
      setSubmitting(null);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSendingMsg(true);
    try {
      const res = await api.post(`/challenges/${id}/judge-discussion`, { content: newMessage });
      setDiscussion(prev => [...prev, res.data]);
      setNewMessage('');
    } catch {
      toast.error('Could not send message.');
    } finally {
      setSendingMsg(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-12 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!panel) return null;

  const { challenge, entries } = panel;
  const totalPicked = Object.keys(picks).length;
  const totalCategories = challenge.categories.length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="judge-panel-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <Gavel className="w-5 h-5 text-brand-accent" />
            <h1 className="font-serif text-2xl font-semibold text-text-primary">Judge Panel</h1>
          </div>
          <p className="text-text-secondary text-sm">{challenge.title}</p>
        </div>
        <Badge className="bg-amber-100 text-amber-700">
          {totalPicked}/{totalCategories} categories picked
        </Badge>
      </div>

      <Tabs defaultValue="entries">
        <TabsList className="bg-surface-alt mb-6">
          <TabsTrigger value="entries" className="gap-2">
            <BookOpen className="w-4 h-4" />
            Entries ({entries.length})
          </TabsTrigger>
          <TabsTrigger value="vote" className="gap-2">
            <Award className="w-4 h-4" />
            Vote
          </TabsTrigger>
          <TabsTrigger value="discussion" className="gap-2">
            <MessageCircle className="w-4 h-4" />
            Discussion
          </TabsTrigger>
        </TabsList>

        {/* Entries Tab — Blind reading */}
        <TabsContent value="entries">
          <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 font-medium">
              Blind Judging Mode — Author names are hidden. Read each entry on its own merit.
            </p>
          </div>
          <div className="grid gap-4">
            {entries.map((entry) => (
              <Card
                key={entry.entry_id}
                className={`cursor-pointer card-hover border-brand-primary/10 ${selectedEntry?.entry_id === entry.entry_id ? 'ring-2 ring-brand-accent' : ''}`}
                onClick={() => setSelectedEntry(selectedEntry?.entry_id === entry.entry_id ? null : entry)}
                data-testid={`blind-entry-${entry.entry_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-lg font-semibold text-text-primary">
                      Entry #{entry.entry_number}: {entry.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-text-muted">
                      {entry.genre && <Badge variant="outline">{entry.genre}</Badge>}
                      {entry.word_count > 0 && <span>{entry.word_count.toLocaleString()} words</span>}
                    </div>
                  </div>
                  {selectedEntry?.entry_id === entry.entry_id && (
                    <div className="mt-4 pt-4 border-t border-brand-primary/10">
                      <div
                        className="prose prose-sm max-w-none font-reading text-text-primary"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.content, { ADD_TAGS: ['img', 'h1', 'h2', 'h3', 'u', 'hr', 'blockquote', 'ol', 'ul', 'li'], ADD_ATTR: ['src', 'alt', 'style'] }) }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {entries.length === 0 && (
              <div className="text-center py-12 text-text-muted">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No entries yet.</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Vote Tab */}
        <TabsContent value="vote">
          <div className="space-y-6">
            {challenge.categories.map((category) => (
              <Card key={category} className="border-brand-primary/10" data-testid={`vote-category-${category}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-4 h-4 text-brand-accent" />
                    <h3 className="font-serif text-lg font-semibold text-text-primary">{category}</h3>
                    {picks[category] && (
                      <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                    )}
                  </div>
                  <p className="text-sm text-text-muted mb-4">Select your pick for this category:</p>
                  <div className="flex items-center gap-3">
                    <Select
                      value={picks[category] || ''}
                      onValueChange={(val) => handlePick(category, val)}
                      disabled={!!submitting}
                    >
                      <SelectTrigger className="flex-1" data-testid={`pick-select-${category}`}>
                        <SelectValue placeholder="Choose an entry..." />
                      </SelectTrigger>
                      <SelectContent>
                        {entries.map((entry) => (
                          <SelectItem key={entry.entry_id} value={entry.entry_id}>
                            Entry #{entry.entry_number}: {entry.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {submitting === category && <Loader2 className="w-4 h-4 animate-spin text-brand-accent" />}
                    {picks[category] && !submitting && <Check className="w-5 h-5 text-green-500" />}
                  </div>
                </CardContent>
              </Card>
            ))}
            {challenge.categories.length === 0 && (
              <p className="text-center text-text-muted py-8">No categories set for this challenge yet.</p>
            )}
          </div>
        </TabsContent>

        {/* Discussion Tab — Private judge chat */}
        <TabsContent value="discussion">
          <Card className="border-brand-primary/10">
            <CardContent className="p-0">
              <div className="p-4 border-b border-brand-primary/10 bg-surface-alt/50">
                <p className="text-sm text-text-muted font-medium">
                  Private discussion — only judges and admins can see this
                </p>
              </div>
              <div className="max-h-[400px] overflow-y-auto p-4 space-y-3" data-testid="judge-discussion-messages">
                {discussion.length === 0 && (
                  <p className="text-center text-text-muted py-8 text-sm">
                    No messages yet. Start the conversation about the entries.
                  </p>
                )}
                {discussion.map((msg) => {
                  const isMe = msg.author_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] ${isMe ? '' : 'flex gap-2'}`}>
                        {!isMe && (
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className="bg-brand-primary/10 text-brand-primary text-xs">
                              {msg.author_name?.[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div>
                          <div className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
                            isMe
                              ? 'bg-brand-primary text-white rounded-br-md'
                              : 'bg-surface-alt text-text-primary rounded-bl-md'
                          }`}>
                            {msg.content}
                          </div>
                          <span className={`text-[10px] text-text-muted mt-1 block ${isMe ? 'text-right' : ''}`}>
                            {isMe ? 'You' : msg.author_name} &bull; {new Date(msg.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={discussionEndRef} />
              </div>
              <form onSubmit={handleSendMessage} className="border-t border-brand-primary/10 p-4 flex gap-3">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Discuss entries, ties, concerns..."
                  rows={2}
                  className="flex-1 resize-none"
                  data-testid="judge-discussion-input"
                />
                <Button
                  type="submit"
                  className="btn-accent self-end"
                  disabled={sendingMsg || !newMessage.trim()}
                  data-testid="judge-discussion-send"
                >
                  {sendingMsg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JudgePanelPage;
